'use client';

import { useEffect } from 'react';
import { apiPost } from '@/lib/api-client';

/**
 * Sends client-side errors to the server so they appear in docker logs.
 * Catches: uncaught errors, unhandled promise rejections (e.g. chunk load failures).
 */
export function ClientErrorReporter() {
  useEffect(() => {
    const report = (source: string, message: string, stack?: string, extra?: unknown) => {
      try {
        apiPost('/api/log-client-error', {
          source,
          message,
          stack: stack?.slice(0, 2000),
          url: typeof window !== 'undefined' ? window.location.href : '',
          extra,
        }).catch(() => {});
      } catch {
        // Ignore - we're in error handling
      }
      console.error(`[ClientErrorReporter] ${source}:`, message, stack, extra);
    };

    const handleError = (e: ErrorEvent) => {
      const msg = e.message || String(e.error);
      const stack = e.error?.stack;
      report('window.onerror', msg, stack, { filename: e.filename, lineno: e.lineno });
    };

    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      const msg = e.reason?.message ?? String(e.reason);
      const stack = e.reason?.stack;
      report('unhandledrejection', msg, stack);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}
